import {
  FlexBoxImageScaleView,
  FlexBoxView,
  FlexNodeStyle,
} from '@play-co/timestep-core/flexbox';
import { ImageScaleView, ReflowReason, View } from '@play-co/timestep-core/ui';
import gsap from 'gsap';
import { downscale, DOWNSCALE_FACTOR } from 'src/conf/scale';

const assets = {
  panel: 'assets/ui/shared/tooltip/tooltip_frame.png',
  tip: 'assets/ui/shared/tooltip/tooltip_tip.png',
};

type TooltipDirection = 'up' | 'down';

export type TooltipOpts = {
  /**
   * Parent view to attach the tooltip to
   */
  superview: View;

  /**
   * Optional width of the tooltip panel,
   */
  width?: number;

  /**
   * Optional height of the tooltip panel,
   */
  height?: number;

  /**
   * Horizontal position of the tooltip relative to the parent view
   */
  x?: number;

  /**
   * Vertical position of the tooltip relative to the parent view
   */
  y?: number;

  /**
   * Base scale of the tooltip
   */
  scale?: number;

  /**
   * Direction the tooltip is facing
   * ie. 'up' or 'down',
   * defaults to 'down'
   */
  direction?: TooltipDirection;

  /**
   * Vertical offset from the master position in both directions
   * according to the direction the tooltip is coming out from,
   * defaults to 10
   */
  tooltipOffsetY?: number;

  /**
   * Horizontal ratio of the panel placement in relation to the panel body
   * ie. 0 - left, 0.5 - center, 1 - right, and anywhere in between,
   * defaults to 0.5
   */
  tipAnchorX?: number;

  /** z-index of the master tooltip view */
  zIndex?: number;

  /**
   * Flexbox style for the content container view
   */
  flex?: FlexNodeStyle;
};

const tipWidth = downscale(70);
const tipHeight = downscale(39);

/**
 * Base tooltip component with exposed content container to allow content injection.
 * Can be shown/hidden with animation and positioned above/below a given point,
 * as well as aligned horizontally via tip anchor point.
 */
export class Tooltip extends View {
  protected readonly panel: FlexBoxView;
  protected readonly background: FlexBoxImageScaleView;
  protected readonly tip: ImageScaleView;
  public readonly content: FlexBoxView;
  protected direction!: TooltipDirection;
  protected baseScale: number;
  protected tooltipOffsetY: number;
  protected tipAnchorX: number;
  protected _tooltipShown = false;

  public get isShown() {
    return this._tooltipShown;
  }

  constructor(opts: TooltipOpts) {
    const {
      superview,
      width,
      height,
      x = 0,
      y = 0,
      scale = 1,
      direction = 'down',
      tooltipOffsetY = 0,
      tipAnchorX = 0.5,
      flex,
      zIndex = 1000,
    } = opts;

    super({
      superview,
      x,
      y,
      infinite: true,
      canHandleEvents: false,
      visible: false,
      scale: 0,
      zIndex,
    });

    this.baseScale = scale;
    this.tooltipOffsetY = tooltipOffsetY;
    this.tipAnchorX = tipAnchorX;

    // Create master container of the tooltip
    this.panel = new FlexBoxView({ superview: this });

    // Create panel background
    this.background = new FlexBoxImageScaleView({
      superview: this.panel,
      image: assets.panel,
      imgScale: DOWNSCALE_FACTOR,
      scaleMethod: '9slice',
      sourceSlices: {
        horizontal: { left: 60, right: 60 },
        vertical: { top: 60, bottom: 60 },
      },
    });

    // Create tip graphic
    this.tip = new ImageScaleView({
      superview: this,
      image: assets.tip,
      width: tipWidth,
      height: tipHeight,
      anchorX: 0.5 * tipWidth,
      offsetX: -0.5 * tipWidth,
    });

    /**
     * Create a content wrapper to visually fit the panel background,
     * accommodating for empty space and shadow.
     */
    const wrapper = new FlexBoxView({
      superview: this.background,
      flex: {
        paddingTop: 17,
        paddingBottom: 10,
        paddingLeft: 6,
        paddingRight: 6,
        width: width ?? 'auto',
        height: height ?? 'auto',
      },
    });

    // Create the container where content will be fitted into
    this.content = new FlexBoxView({
      superview: wrapper,
      flex: {
        justifyContent: 'center',
        alignItems: 'center',
        padding: 30,
        ...flex,
      },
    });

    const originalReflow = this.content.reflow.bind(this.content);
    this.content.reflow = (reason: ReflowReason) => {
      originalReflow(reason);
      this.repositionContent();
    };

    this.setDirection(direction);
  }

  /**
   * Show and animates in the tooltip from the origin
   */
  public async showTooltip(opts?: unknown) {
    this._tooltipShown = true;
    this.updateOpts({ visible: true, scale: 0 });
    await gsap.to(this.style, {
      duration: 0.2,
      scale: this.baseScale,
      ease: 'back.out',
      overwrite: true,
    });
  }

  /**
   * Animates out the tooltip to the origin and hides it
   */
  public async hideTooltip(instant = false) {
    if (!this.isShown || this.style.scale < this.baseScale) return;

    if (!instant) {
      await gsap.to(this.style, {
        duration: 0.1,
        scale: 0,
        ease: 'quad.inOut',
        overwrite: true,
      });
    }

    this.updateOpts({ visible: false });
    this._tooltipShown = false;
  }

  /**
   * Sets the horizontal anchor point of the tooltip tip.
   */
  public setTipAnchorX(ratio: number) {
    this.tipAnchorX = ratio;
    this.repositionContent();
  }

  /**
   * Sets the direction of the tooltip from the origin.
   */
  public setDirection(direction: 'up' | 'down') {
    // Skip if direction is unchanged
    if (this.direction === direction) return;
    this.direction = direction;
    this.repositionContent();
  }

  protected repositionContent() {
    // Pads the anchor offset, accounting for panel corners
    const cornerRadius = 23;
    const width = this.background.style.width - cornerRadius * 2 - tipWidth;
    const anchorX = cornerRadius + tipWidth * 0.5 + width * this.tipAnchorX;

    // Reposition and re-anchor the master container based on direction
    this.panel.updateOpts({
      anchorX: anchorX,
      offsetX: -anchorX,
      y:
        this.direction === 'up'
          ? 19 - tipHeight - this.tooltipOffsetY - this.background.style.height
          : tipHeight + this.tooltipOffsetY - 26,
    });

    // Reposition and orient the tip graphic based on direction
    this.tip.updateOpts({
      r: this.direction === 'up' ? Math.PI : 0,
      y: this.direction === 'up' ? -this.tooltipOffsetY : this.tooltipOffsetY,
    });
  }
}
